import { EventEmitter, Injectable } from '@angular/core';
import { Product } from '../../models/product.model';
import { CartItem } from '../../models/cart-item.model';
import { MessageService } from '../../messages/message.service';

@Injectable()
export class CartService {
  // Init and generate some fixtures
  private cartItems: CartItem[];
  public itemsChanged: EventEmitter<CartItem[]> = new EventEmitter<CartItem[]>();

  constructor(private messageService: MessageService) {
    this.cartItems = [];
  }

  public getItems() {
    return this.cartItems.slice();
  }

  public updateItem(item: CartItem) {
    const existingItem = this.cartItems.find(cartItem =>
      cartItem.product.id === item.product.id &&
      (item.option ? cartItem.option?.id === item.option.id : !cartItem.option)
    );

    if (existingItem) {
      // Update existing item
      existingItem.quantity = item.quantity;
      existingItem.price = item.price;
      this.messageService.add(`Updated ${item.product.name}${item.option ? ` (Option: ${item.option.id})` : ''} in cart`);
    } else {
      // Add new item if it doesn't exist
      this.cartItems.push(item);
      this.messageService.add(`Added ${item.product.name}${item.option ? ` (Option: ${item.option.id})` : ''} to cart`);
    }

    // Emit updated cart items
    this.itemsChanged.emit(this.cartItems.slice());
  }

  public addItem(item: CartItem) {
    const existingItem = this.cartItems.find(cartItem =>
      cartItem.product.id === item.product.id &&
      (item.option ? cartItem.option?.id === item.option.id : !cartItem.option)
    );

    if (existingItem) {
      // If item exists, update its price and quantity
      existingItem.price += item.price;
      existingItem.quantity += item.quantity;
      this.messageService.add(`Price updated for ${item.product.name}${item.option ? ` (Option: ${item.option.id})` : ''} in cart`);
    } else {
      // If item doesn't exist, add it to cart
      this.cartItems.push(item);
      this.messageService.add(`Added ${item.product.name}${item.option ? ` (Option: ${item.option.id})` : ''} to cart`);
    }

    // Emit updated cart items
    this.itemsChanged.emit(this.cartItems.slice());
  }

  public addItems(items: CartItem[]) {
    items.forEach((cartItem) => {
      this.addItem(cartItem);
    });
  }

  public removeItem(item: CartItem) {
    const indexToRemove = this.cartItems.findIndex(cartItem =>
      cartItem.product.id === item.product.id &&
      (item.option ? cartItem.option?.id === item.option.id : !cartItem.option)
    );

    if (indexToRemove !== -1) {
      const removedItem = this.cartItems.splice(indexToRemove, 1)[0];
      this.messageService.add(`Removed ${removedItem.product.name}${removedItem.option ? ` (Option: ${removedItem.option.id})` : ''} from cart`);
      this.itemsChanged.emit(this.cartItems.slice());
    } else {
      this.messageService.add(`Item ${item.product.name}${item.option ? ` (Option: ${item.option.id})` : ''} not found in cart`);
    }
  }

  public increaseQuantity(item: CartItem) {
    const existingItem = this.cartItems.find(cartItem =>
      cartItem.product.id === item.product.id &&
      (item.option ? cartItem.option?.id === item.option.id : !cartItem.option)
    );

    if (existingItem) {
      existingItem.quantity += 1;
      existingItem.price = existingItem.quantity * (existingItem.price / existingItem.quantity);
      this.messageService.add(`Increased quantity for ${existingItem.product.name}${existingItem.option ? ` (Option: ${existingItem.option.id})` : ''} to ${existingItem.quantity}`);
      this.itemsChanged.emit(this.cartItems.slice());
    } else {
      this.messageService.add(`Item ${item.product.name}${item.option ? ` (Option: ${item.option.id})` : ''} not found in cart`);
    }
  }

  public decreaseQuantity(item: CartItem) {
    const existingItem = this.cartItems.find(cartItem =>
      cartItem.product.id === item.product.id &&
      (item.option ? cartItem.option?.id === item.option.id : !cartItem.option)
    );

    if (existingItem) {
      existingItem.quantity -= 1;
      if (existingItem.quantity <= 0) {
        const indexToRemove = this.cartItems.findIndex(cartItem =>
          cartItem.product.id === item.product.id &&
          (item.option ? cartItem.option?.id === item.option.id : !cartItem.option)
        );
        this.cartItems.splice(indexToRemove, 1);
        this.messageService.add(`Removed ${existingItem.product.name}${existingItem.option ? ` (Option: ${existingItem.option.id})` : ''} from cart`);
      } else {
        existingItem.price = existingItem.quantity * (existingItem.price / existingItem.quantity);
        this.messageService.add(`Decreased quantity for ${existingItem.product.name}${existingItem.option ? ` (Option: ${existingItem.option.id})` : ''} to ${existingItem.quantity}`);
      }
      this.itemsChanged.emit(this.cartItems.slice());
    } else {
      this.messageService.add(`Item ${item.product.name}${item.option ? ` (Option: ${item.option.id})` : ''} not found in cart`);
    }
  }

  public updateItemprice(item: CartItem, newprice: number) {
    this.cartItems.forEach((cartItem) => {
      if (
        cartItem.product.id === item.product.id &&
        (item.option ? cartItem.option?.id === item.option.id : !cartItem.option)
      ) {
        cartItem.price = newprice;
      }
    });
    this.itemsChanged.emit(this.cartItems.slice());
    this.messageService.add(`Updated price for ${item.product.name}${item.option ? ` (Option: ${item.option.id})` : ''}`);
  }

  public clearCart() {
    this.cartItems = [];
    this.itemsChanged.emit(this.cartItems.slice());
    this.messageService.add('Cleared cart');
  }

  public getTotal() {
    let total = 0;
    this.cartItems.forEach((cartItem) => {
      total += cartItem.price * cartItem.quantity;
    });
    return total;
  }
}
